import { useState, useEffect, useRef } from 'react';
import type { TouchEvent, MouseEvent } from 'react';
import { useStyles } from '@archipro-design/aria';
import * as S from './PaintPickerShell.style';
import type { DragEndEvent, DragMoveEvent } from '@dnd-kit/core';
import {
    DndContext,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import type { Coordinates, RGB } from '../../type';
import { calculateColourAtPoint } from '../../util/paint-picker-util';
import { DraggablePreview } from '@modules/paint-picker-new/component/draggable-preview/DraggablePreview';
import { useScroll, useEventListener, useDebounceFn } from 'ahooks';
import { drawImageOnCanvas } from '@modules/paint-picker-new/util/draw-image-on-canvas';
import {
    DEBOUNCE_COLOR_DETECT_MOBILE,
    PALETTE_DEFAULT_LIMIT,
} from '@modules/paint-picker-new/config';
import { useLogger } from '@archipro-website/logger/client';
import { usePaintPickerContext } from '../../context/PaintPickerContext';
import { usedRandomPaintFromImage } from '@modules/paint-picker-new/util/get-random-paint-from-image';

interface PaintPickerMobileShellProps {
    imageRef: HTMLImageElement | null;
}

const PaintPickerMobileShell = ({ imageRef }: PaintPickerMobileShellProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [coordinates, setCoordinates] = useState<Coordinates>({ x: 0, y: 0 });
    const [color, setColor] = useState<RGB | null>(null);

    const {
        state: { activeImageSrc, populateDefaults },
        matchRgbToReseneColour,
        onSelectPreview,
        closePaintPicker,
        updatePaintSelection,
    } = usePaintPickerContext();

    const scroll = useScroll();
    const logger = useLogger();
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
    const { css } = useStyles();

    const { run: decouncedPickerClose } = useDebounceFn(closePaintPicker, {
        wait: 1000,
        leading: true,
    });

    const previewAtCoordinates = (
        { x, y }: Coordinates,
        canvas: HTMLCanvasElement,
        updateCoordinates: boolean,
        fetchProductInfo: boolean
    ): void => {
        const color = calculateColourAtPoint(x, y, canvas);
        if (updateCoordinates) {
            setCoordinates({ x, y });
        }

        setColor(color);

        const paint = color ? matchRgbToReseneColour(color) : null;
        if (paint) {
            onSelectPreview(paint, fetchProductInfo);
        }
    };

    const { run: debouncePreview } = useDebounceFn(
        (
            coords: Coordinates,
            canvas: HTMLCanvasElement,
            updateCoordinates: boolean,
            fetchProductInfo: boolean
        ) => {
            previewAtCoordinates(
                coords,
                canvas,
                updateCoordinates,
                fetchProductInfo
            );
        },
        {
            wait: DEBOUNCE_COLOR_DETECT_MOBILE,
            maxWait: DEBOUNCE_COLOR_DETECT_MOBILE,
            trailing: true,
        }
    );

    const onDragEnd = (event: DragEndEvent) => {
        if (canvasRef?.current) {
            const offset = scroll?.top ?? 0;
            const newCoords = {
                x: coordinates.x + event.delta.x,
                y: coordinates.y + event.delta.y - offset,
            };
            debouncePreview(newCoords, canvasRef.current, true, true);
        }
    };

    const onDragMove = (event: DragMoveEvent) => {
        if (canvasRef?.current) {
            const offset = scroll?.top ?? 0;
            const newCoords = {
                x: coordinates.x + event.delta.x,
                y: coordinates.y + event.delta.y - offset,
            };
            previewAtCoordinates(newCoords, canvasRef.current, false, false);
        }
    };

    const onTouch = (
        event: TouchEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>
    ) => {
        if (canvasRef?.current) {
            const box = event.currentTarget.getBoundingClientRect();
            const clientX =
                'touches' in event
                    ? event.touches[0]?.clientX ?? 0
                    : event.clientX;
            const clientY =
                'touches' in event
                    ? event.touches[0]?.clientY ?? 0
                    : event.clientY;
            const newCoords = {
                x: clientX - box.left,
                y: clientY - box.top,
            };
            debouncePreview(newCoords, canvasRef.current, true, true);
        }
    };

    useEventListener('scroll', decouncedPickerClose, { target: window });

    useEffect(() => {
        (async () => {
            if (!imageRef || !canvasRef.current) {
                return;
            }

            await drawImageOnCanvas(canvasRef.current, imageRef).then(
                () => {
                    if (
                        canvasRef.current &&
                        canvasRef.current.clientWidth > 0 &&
                        canvasRef.current.clientHeight > 0
                    ) {
                        previewAtCoordinates(
                            {
                                x: canvasRef.current.clientWidth / 2,
                                y: canvasRef.current.clientHeight / 2,
                            },
                            canvasRef.current,
                            true,
                            true
                        );

                        if (populateDefaults && activeImageSrc) {
                            const paints = usedRandomPaintFromImage(
                                canvasRef.current,
                                matchRgbToReseneColour,
                                PALETTE_DEFAULT_LIMIT,
                                activeImageSrc
                            );

                            updatePaintSelection(paints);
                        }
                    }
                },
                (error) => {
                    logger.error('could not draw image canvas', error);
                }
            );
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageRef]);

    return (
        <div
            className={css(S.PaintPickerMobileShellStyle)}
            onTouchStart={onTouch}
            onClick={onTouch}
        >
            <canvas ref={canvasRef} className={css(S.CanvasStyling)} />
            <DndContext
                sensors={sensors}
                onDragEnd={onDragEnd}
                onDragMove={onDragMove}
                autoScroll={{ enabled: false }}
            >
                <DraggablePreview coordinates={coordinates} color={color} />
            </DndContext>
        </div>
    );
};

export default PaintPickerMobileShell;
